import { formatToBRL } from "@/utils/formatToMoney";
import { ColumnDef } from "@tanstack/react-table";
import { DataTableColumnHeader } from "./data-table-column-header";
import { User } from "../../data/schema";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";
import { toast } from "components/ui/use-toast";
import { Copy } from "lucide-react";

export const columns: ColumnDef<User>[] = [
  {
    accessorKey: "store",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Loja" />
    ),
  },
  {
    accessorKey: "release_sequence",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Sequência" />
    ),
    cell: ({ row }) => (
      <div className=" w-full flex justify-center items-center">
        <span>{row.getValue("release_sequence")}</span>
      </div>
    ),
  },
  {
    accessorKey: "operation_type",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Tipo de Operação" />
    ),
    cell: ({ row }) => {
      const value = row.getValue("operation_type");
      return value ? value : "--";
    },
  },
  {
    accessorKey: "payment_type",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Tipo de Pagamento" />
    ),
    cell: ({ row }) => {
      const value = row.getValue("payment_type");
      return value ? value : "--";
    },
  },
  {
    accessorKey: "operation_number",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Número" />
    ),
  },
 {
  accessorKey: "nsu_tef",
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="NSU" />
  ),
  cell: ({ row }) => {
    const nsuValue = row.getValue("nsu_tef") as string;
    
    const handleCopyNsu = async () => {
      if (!nsuValue) return;
      
      try {
        await navigator.clipboard.writeText(nsuValue);
        toast({
          title: "NSU copiado!",
          description: `NSU ${nsuValue} copiado para a área de transferência.`,
          duration: 2000,
        });
      } catch (err) {
        toast({
          variant: "destructive",
          title: "Erro ao copiar",
          description: "Não foi possível copiar o NSU.",
          duration: 2000,
        });
      }
    };

    return (
      <div 
        className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 rounded px-2 py-1 transition-colors group"
        onClick={handleCopyNsu}
        title="Clique para copiar"
      >
        <span className="select-none">{nsuValue || "--"}</span>
        <Copy className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity text-gray-500" />
      </div>
    );
  },
},
  {
    accessorKey: "value",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Valor" />
    ),
    cell: ({ row }) => {
      const value = row.getValue("value");
      return formatToBRL(Number(value) || 0);
    },
  },
  {
    accessorKey: "operation_date",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Data" />
    ),
    cell: ({ row }) => {
      const date = row.getValue("operation_date");
      if (date) {
        const parsedDate = new Date(date as string | number | Date);

        if (!isNaN(parsedDate.getTime())) {
          const offsetBrasilia = -3;
          const adjustedDate = new Date(
            parsedDate.getTime() + offsetBrasilia * 60 * 60 * 1000
          );

          const day = String(adjustedDate.getUTCDate()).padStart(2, "0");
          const month = String(adjustedDate.getUTCMonth() + 1).padStart(2, "0");
          const year = adjustedDate.getUTCFullYear();

          const formattedDate = `${day}/${month}/${year}`;
          return formattedDate;
        } else {
          console.error("Invalid date value:", date);
          return "Invalid date";
        }
      } else {
        console.error("Date value is null or undefined");
        return "Sem data";
      }
    },
  },
  {
    accessorKey: "user",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Usuário" />
    ),
  },
  {
    accessorKey: "observation",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Observação" />
    ),
    cell: ({ row }) => (
      <div className="w-full flex items-center justify-start">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              {row.getValue("observation") === null ? (
                <p className="text-gray-400">-</p>
              ) : (
                <p className="max-w-[150px] truncate whitespace-nowrap overflow-hidden">
                  {row.getValue("observation")}
                </p>
              )}
            </TooltipTrigger>
            <TooltipContent className="bg-[#141414] text-white text-center max-w-[250px] p-2">
              <p className="whitespace-pre-wrap break-words">
                {row.getValue("observation")}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    ),
  },
];
